import { useEffect, useState } from "react"

import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import { saveAboutMeApi } from "@/api/home-context"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { useHomeContext } from "@/features/chat/hooks/use-home-context"
import { usePrismMemory } from "@/hooks/use-prism-memory"
import { logger } from "@/lib/logger"
import { SettingsService } from "@/services/settings-service"

export const ABOUT_ME_MAX_LEN = 4000

function getSettingsToken(): string | undefined {
  try {
    return SettingsService.getInstance().token
  } catch {
    return undefined
  }
}

export type AboutMeFormProps = {
  active: boolean
  showCancel?: boolean
  onSaved?: () => void
  onCancel?: () => void
  textareaClassName?: string
  containerClassName?: string
  footerClassName?: string
}

export function AboutMeForm({
  active,
  showCancel = false,
  onSaved,
  onCancel,
  textareaClassName,
  containerClassName,
  footerClassName,
}: AboutMeFormProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const prismMemory = usePrismMemory()
  const homeContext = useHomeContext(active)

  const remoteText = homeContext.data?.helloai.user_instructions ?? ""

  // null until the user types; then their draft, including "".
  const [draft, setDraft] = useState<string | null>(null)
  // When this open began. Only data written after it is this open's data.
  const [openedAt, setOpenedAt] = useState<number | null>(null)

  useEffect(() => {
    if (!active) {
      setDraft(null)
      setOpenedAt(null)
      return
    }
    if (prismMemory) {
      const cachedAt = queryClient.getQueryState(["home-context"])?.dataUpdatedAt ?? 0
      setOpenedAt(Math.max(Date.now(), cachedAt))
      // Cancel first so a fetch already in flight without data is not
      // deduplicated: the text Save may post comes from a fetch started now.
      void queryClient.cancelQueries({ queryKey: ["home-context"] })
      void queryClient.invalidateQueries({ queryKey: ["home-context"] })
    }
  }, [active, prismMemory, queryClient])

  // One invariant, derived on every render and never latched. In Prism mode
  // Save posts only what the user typed, or text from a fetch that completed
  // successfully after this open. A pre-open cache, a fetch still running or
  // paused (hidden tab, offline), and a failed post-open fetch all leave
  // dataUpdatedAt at or before openedAt. openedAt is null outside Prism.
  const postOpen = openedAt !== null && homeContext.dataUpdatedAt > openedAt
  // The untyped field shows retained data: TanStack keeps it, with status
  // "error", when a later refetch fails. Legacy shows any loaded data; Prism
  // only data a post-open fetch wrote.
  const shown = homeContext.data !== undefined && (!prismMemory || postOpen)
  // What Save may post untyped. Prism: a post-open fetch that succeeded.
  const saveOwned = prismMemory ? homeContext.isSuccess && postOpen : shown
  const userEdited = draft !== null
  const text = draft ?? (shown ? remoteText : "")
  // A blank only clears About me when the user emptied the field. The server
  // refuses a blank Prism save without this flag.
  const clear = userEdited && text.trim() === ""

  const mutation = useMutation({
    mutationFn: async (next: { text: string; clear: boolean }) => {
      const idToken = getSettingsToken()
      return saveAboutMeApi(next.text, idToken, { clear: next.clear })
    },
    onSuccess: (result) => {
      if ("result" in result && result.result !== "saved") {
        toast.info(t("personalization.memoryPanel.prism.notApplied"))
        return
      }
      void queryClient.invalidateQueries({ queryKey: ["home-context"] })
      toast.success(t("personalization.aboutMe.savedToast"))
      onSaved?.()
    },
    onError: (error) => {
      logger.error("save about-me failed", error as Error)
      toast.error(t("personalization.aboutMe.saveFailed"))
    },
  })

  const overLimit = text.length > ABOUT_ME_MAX_LEN
  // In both modes an untyped Save needs owned text and never posts a blank:
  // only a field the user emptied may clear About me.
  const disabled =
    overLimit || mutation.isPending || (!userEdited && (!saveOwned || text.trim() === ""))

  return (
    <>
      <div className={containerClassName ?? "flex flex-col gap-2"}>
        {prismMemory && homeContext.data?.helloai.about_me_not_applied == null ? (
          <p className="text-muted-foreground text-sm" role="status">
            {t("personalization.aboutMe.statusUnknown")}
          </p>
        ) : null}
        {homeContext.data?.helloai.about_me_not_applied === true ? (
          <p className="text-muted-foreground text-sm" role="status">
            {t("personalization.aboutMe.notAppliedPersistent")}
          </p>
        ) : null}
        <Textarea
          className={textareaClassName ?? "min-h-[260px]"}
          data-testid="about-me-textarea"
          placeholder={t("personalization.aboutMe.placeholder")}
          value={text}
          onChange={(e) => setDraft(e.target.value)}
        />
        <div
          className={
            overLimit
              ? "text-destructive text-right text-xs"
              : "text-muted-foreground text-right text-xs"
          }
        >
          {t("personalization.aboutMe.characterCount", { count: text.length })}
        </div>
      </div>

      <div className={footerClassName ?? "flex flex-row justify-end gap-2"}>
        {showCancel ? (
          <Button variant="outline" onClick={() => onCancel?.()}>
            {t("personalization.aboutMe.cancel")}
          </Button>
        ) : null}
        <Button
          data-testid="about-me-save"
          disabled={disabled}
          onClick={() => mutation.mutate({ text, clear })}
        >
          {t("personalization.aboutMe.save")}
        </Button>
      </div>
    </>
  )
}
